// Kpi — canonical headline-tile component.
//
// Replaces the 33 inline Kpi/Stat/Tile copies scattered across src/pages/.
// Each copy diverged on one or more axes (tone enum vs raw className, dark
// vs light surface, required vs optional tone, with-icon vs without, with-sub
// vs without). This component carries the UNION of all those shapes so any
// inline copy can be migrated mechanically. Migrations land via the Phase 4
// opportunistic burn-down — see REFACTOR-BACKLOG.md.
//
// Default look matches the most common variant — the `.card` container with a
// small label, medium-weight value, optional sub line. Use `surface='dark'`
// for FloorMonitor-style displays (5-10m viewing distance), `surface='light'`
// for printed reports.
//
// tone accepts BOTH semantic names ('good'/'warn'/'bad'/'info'/'neutral') AND
// the color names existing inline copies used ('green'/'emerald'/'amber'/'red'
// /'blue'/'cyan'/'gray'). They map to the same color families. For the inline
// copies that pass a free-form Tailwind class string (Labour.Tile,
// FarmEstimates.Kpi, TransportRecon.Kpi), use the `toneClassName` escape
// hatch — that string fully overrides the value-color class so existing
// copies migrate without a visual diff.

import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import clsx from 'clsx'

export type KpiTone =
  // Semantic — recommend these for new code.
  | 'good' | 'warn' | 'bad' | 'info' | 'neutral'
  // Color aliases — accepted so inline copies migrate without renaming.
  | 'green' | 'emerald' | 'amber' | 'red' | 'blue' | 'cyan' | 'gray'

export type KpiSurface = 'card' | 'dark' | 'light'
export type KpiSize    = 'sm' | 'md' | 'lg' | 'xl'

export interface KpiProps {
  label:    string
  value:    ReactNode
  /** Small line below the value. Accepts `subtitle` and `hint` as aliases. */
  sub?:     ReactNode
  subtitle?: ReactNode
  hint?:    ReactNode
  /** Semantic tone OR color alias. Defaults to 'neutral'. */
  tone?:    KpiTone
  /** Alias for tone — some inline copies named the prop `variant` or `color`. */
  variant?: KpiTone
  color?:   KpiTone
  /** RunDetail-style sugar — `highlight={true}` resolves to tone='good'. */
  highlight?: boolean
  /** Lucide icon rendered next to the label (FarmCostReport.Stat pattern). */
  icon?:    LucideIcon
  /** Container preset. Default 'card'. */
  surface?: KpiSurface
  /** Value-text size. Default 'md'. 'xl' is the FloorMonitor 5xl size. */
  size?:    KpiSize
  /** Escape hatch — when set, replaces the tone-derived value-color class
   *  entirely. Use for the inline copies that pass a raw Tailwind className
   *  via tone (Labour.Tile, FarmEstimates.Kpi, TransportRecon.Kpi). */
  toneClassName?: string
  /** Extra classes on the outer container. */
  className?: string
  /** Makes the tile a BUTTON (a filter the tile's figure describes — "tap to
   *  show the unrated rows"). Keyboard-reachable, announced as a button; the
   *  label + value stay the accessible name. Never for navigation — link the
   *  label instead. */
  onClick?: () => void
  /** With `onClick`: the filter this tile applies is currently on. Renders the
   *  ring and `aria-pressed`, so the tile also reads as the toggle it is. */
  active?: boolean
}

// Semantic-tone → color family. 'green'/'emerald' collapse to 'good',
// 'amber' to 'warn', 'red' to 'bad', 'blue'/'cyan' to 'info', 'gray' to
// 'neutral'.
function normaliseTone(t: KpiTone | undefined): 'good' | 'warn' | 'bad' | 'info' | 'neutral' {
  switch (t) {
    case 'good':
    case 'green':
    case 'emerald':
      return 'good'
    case 'warn':
    case 'amber':
      return 'warn'
    case 'bad':
    case 'red':
      return 'bad'
    case 'info':
    case 'blue':
    case 'cyan':
      return 'info'
    case 'gray':
    case 'neutral':
    default:
      return 'neutral'
  }
}

// Surface × tone → value text-color class. Light + dark + card each have
// their own palette so the same tone reads correctly against the background.
const VALUE_COLOR: Record<KpiSurface, Record<ReturnType<typeof normaliseTone>, string>> = {
  card: {
    good:    'text-emerald-300',
    warn:    'text-amber-300',
    bad:     'text-red-400',
    info:    'text-cyan-300',
    neutral: 'text-gray-100 light:text-fg',
  },
  dark: {
    good:    'text-emerald-400',
    warn:    'text-amber-400',
    bad:     'text-red-400',
    info:    'text-cyan-400',
    neutral: 'text-gray-200',
  },
  light: {
    good:    'text-emerald-700',
    warn:    'text-amber-700',
    bad:     'text-red-700',
    info:    'text-blue-700',
    neutral: 'text-gray-900',
  },
}

// Surface → container class. 'card' uses the project's .card utility so it
// inherits whatever the design-system gives that class. 'dark' is the
// FloorMonitor display variant. 'light' is the printable-report variant.
const CONTAINER: Record<KpiSurface, string> = {
  card:  'card p-3',
  dark:  'surface-sunk/50 rounded-3xl border hairline p-4',
  light: 'bg-gray-50 border border-gray-300 rounded-lg p-3',
}

const VALUE_SIZE: Record<KpiSize, string> = {
  sm: 'text-sm font-semibold',
  md: 'text-base font-bold',
  lg: 'text-2xl font-bold',
  xl: 'text-5xl font-bold tracking-tight',
}

// ⚠️ `light` keeps text-gray-500/600 ON PURPOSE. .text-muted (#8b93a1) is a
// DARK-SHELL fix: on white it regresses 4.83:1 to 3.09:1. Same rule as
// src/index.css — the surface decides, not the token.
const LABEL_COLOR: Record<KpiSurface, string> = {
  card:  'text-muted',
  dark:  'text-muted',
  light: 'text-gray-600',
}

export function Kpi(props: KpiProps) {
  const {
    label, value, sub, subtitle, hint,
    tone, variant, color, highlight,
    icon: Icon,
    surface = 'card',
    size = 'md',
    toneClassName,
    className,
    onClick,
    active,
  } = props

  // tone | variant | color are aliases — first non-undefined wins. highlight
  // is sugar: when true, force tone='good' so RunDetail.Stat migrates with
  // `highlight={someBool}` unchanged.
  const resolvedTone = highlight ? 'good' : (tone ?? variant ?? color)
  const semantic = normaliseTone(resolvedTone)
  const valueColor = toneClassName ?? VALUE_COLOR[surface][semantic]
  const subContent = sub ?? subtitle ?? hint

  const body = (
    <>
      <p className={clsx('text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1', LABEL_COLOR[surface])}>
        {Icon && <Icon size={10} />}
        <span>{label}</span>
      </p>
      <p className={clsx('tabular-nums', VALUE_SIZE[size], valueColor)}>
        {value}
      </p>
      {/* The sub line's ternary was dead — both arms said text-gray-500, so a
          tile on the DARK shell was getting the light-surface colour. It now
          does what it plainly meant to: gray-500 passes on white (4.83:1) and
          fails on the shell (3.03:1), which is what .text-muted exists for. */}
      {subContent != null && subContent !== '' && (
        <p className={clsx('text-[10px] mt-0.5', surface === 'light' ? 'text-gray-500' : 'text-muted')}>
          {subContent}
        </p>
      )}
    </>
  )

  // A tappable tile is a real <button>: the same box, plus the ring that says
  // "this is a control" and a pressed state while its filter is on.
  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-pressed={!!active}
        className={clsx(CONTAINER[surface], 'text-left w-full min-h-[44px] cursor-pointer pressable',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60',
          // The pointer is still resting on the tile right after the tap, so
          // the hover ring must not out-rank the "on" ring — the one moment
          // the state matters most is the one hover would mask.
          active ? 'ring-1 ring-amber-400 hover:ring-amber-400' : 'hover:ring-1 hover:ring-gray-600',
          className)}>
        {body}
      </button>
    )
  }
  return <div className={clsx(CONTAINER[surface], className)}>{body}</div>
}
